import { createAudioAnalyser } from 'livekit-client';
import type { AudioAnalyserOptions, RemoteAudioTrack } from 'livekit-client';

export type AudioAnalyser = ReturnType<typeof createAudioAnalyser>;

/**
 * Wraps a single remote agent audio track. Handles attach/detach, mute, volume,
 * and exposes an audio analyser for studios that want to drive visualisations
 * from their own render loop.
 *
 * Survives track replacement: when the agent re-publishes audio (e.g. after a
 * network hiccup), the desired mute state is re-applied on attach.
 *
 * The client never silences the dealer on its own: her audibility is the
 * agent's decision (ADR 0057). The only local control is the player's own mute.
 */
export class DealerAudio {
  private track: RemoteAudioTrack | null = null;
  private element: HTMLAudioElement | null = null;
  private analyser: AudioAnalyser | null = null;
  private muted: boolean;

  constructor(
    private readonly container: HTMLElement,
    initialMuted = false,
  ) {
    this.muted = initialMuted;
  }

  attach(track: RemoteAudioTrack): void {
    this.detach();
    this.track = track;
    this.element = track.attach();
    this.element.style.display = 'none';
    this.container.appendChild(this.element);
    this.applyMuted();
  }

  detach(): void {
    if (this.analyser) {
      void this.analyser.cleanup();
      this.analyser = null;
    }
    if (this.track && this.element) {
      this.track.detach(this.element);
      this.element.remove();
    }
    this.track = null;
    this.element = null;
  }

  /**
   * The player's own mute. Writes `element.muted`, not volume: iOS ignores
   * writes to HTMLMediaElement.volume (reads back 1 always) but honours
   * `muted` (#473). Leaving volume alone also stops unmute from clobbering a
   * host's own setVolume().
   */
  setMuted(muted: boolean): void {
    this.muted = muted;
    this.applyMuted();
  }

  private applyMuted(): void {
    if (this.element) this.element.muted = this.muted;
  }

  setVolume(volume: number): void {
    this.track?.setVolume(volume);
  }

  getAnalyser(options?: AudioAnalyserOptions): AudioAnalyser | null {
    if (!this.track) return null;
    if (!this.analyser) {
      this.analyser = createAudioAnalyser(this.track, options);
    }
    return this.analyser;
  }

  isAttached(): boolean {
    return Boolean(this.track && this.element);
  }
}
